import moment from 'moment-timezone';
import { BadgeVariant } from '@usepepper/ui/atoms/badge';
import {
  convertPriceMicrosToGrossProfit,
  DEFAULT_CURRENCY_CODE,
  formatCurrency,
  formatGrossProfitDisplayValue,
} from '@/utils/money';
import { formatSummaryDateWithYear } from '@/utils/date';
import { formatUnitPriceDisplay } from '@/domain/pricing/utils/pricing-utils';
import { getAbbreviatedPackUnit, getPackDisplayString } from '@/utils/item';
import { getTranslationValue, sanitizeTranslation } from '@/utils/translations';
import {
  OrderEntryLabelLevel,
  OrderEntryPriceEditDisplayHeader,
  PriceEditType,
  SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment,
  SalesRepOverrideExpirationSource,
} from '@/types';
import { PackUnit } from '@/enums';
import {
  PriceEditDisplayCell,
  toPriceEditDisplayCell,
} from '@sales-portal/blocs/utils/price-edit-display';
import { PricingCalculations } from '@/utils/pricing-calculations';
import { toFixedDecimalPercentage } from '@/utils/percentage';

const LEVEL_TO_VARIANT_MAP: Record<OrderEntryLabelLevel, BadgeVariant> = {
  [OrderEntryLabelLevel.Error]: BadgeVariant.ERROR,
  [OrderEntryLabelLevel.Info]: BadgeVariant.DEFAULT,
  [OrderEntryLabelLevel.Warning]: BadgeVariant.WARNING,
};

export interface OrderEntryLabel {
  availability_label?: {
    i18n_key: string;
    level: BadgeVariant;
  };
  essential_item_label?: {
    i18n_key: string;
    level: BadgeVariant;
  };
  lead_time_label?: {
    displayText: string;
    level: BadgeVariant;
  };
}

export interface PriceOverrideInfo {
  activeOverride: PriceEditType;
  defaultGrossProfitDisplay: string;
  defaultPackPriceDisplay: string;
  defaultUnitPriceDisplay?: string;
  editedByName: string | null;
  expiryDateDisplay?: string;
  isTemporaryOverride: boolean;
}

export interface OrderEntryRequestNote {
  collectionId: string;
  persistRequest: boolean;
  request: string | null;
}

export interface RequestNoteSavePayload {
  collectionId: string;
  persistRequest: boolean;
  request: string;
  variantPackUUID: string;
}

export interface OrderEntryTableRowData {
  addedAt: string | null;
  brand: string;
  description: string;
  externalItemId: string;
  grossProfitAmountInMicros: number | null;
  grossProfitDollarDisplayString: string;
  grossProfitPercentage: number | null;
  grossProfitPercentageDisplayString: string;
  imageUrl?: string;
  labels?: OrderEntryLabel;
  maxQuantity: number;
  minQuantity: number;
  name: string;
  onHandQuantity: number | null;
  orderGuideCollectionIds: string[];
  orderGuideLabels: { level: string; orderGuideName: string }[];
  packDisplayString: string;
  packSize: string;
  priceDisplayString: string;
  /**
   * Guide-level display-config headers, stamped onto every row so the
   * item-detail panel (mounted outside this screen's data container) can
   * read them directly from `orderEntryCollection.get(uuid)` — same path
   * it uses for badges. The same array reference is shared across rows.
   */
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[];
  priceEditDisplays: Record<string, PriceEditDisplayCell>;
  priceMicros: number | null;
  priceOverrideInfo?: PriceOverrideInfo;
  requestNotes: OrderEntryRequestNote[];
  sortRank: number;
  unitCount: number;
  unitPriceInfo?: string;
  unitSuffix?: string;
  variantPackUUID: string;
}

const EMPTY_PRICE_EDIT_DISPLAY_HEADERS: OrderEntryPriceEditDisplayHeader[] = [];

/**
 * Extracts unit price info for per-unit price display.
 */
const getUnitPriceInfo = (
  item: SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment,
): string | undefined => {
  const unit = item.variant_pack?.pack?.base_unit as PackUnit | null;
  const unitPriceInMicros =
    item.variant_pack_info?.unit_price_in_micros ?? null;

  // 0 is accepted as a valid value to keep parity with operator portal
  if (unitPriceInMicros === null) {
    return;
  }

  const unitSuffix = unit ? getAbbreviatedPackUnit(unit) : undefined;
  // The server formats the currency only; the unit suffix is translated on device.
  const serverPrice = item.variant_pack_info?.unit_price_display;
  if (serverPrice) {
    return unitSuffix ? `${serverPrice}/${unitSuffix}` : serverPrice;
  }
  return (
    formatUnitPriceDisplay(
      unitPriceInMicros,
      item.variant_pack_info?.currency,
      unitSuffix,
    ) || undefined
  );
};

/**
 * Transforms a raw order entry item from the API into table row data.
 *
 * This function extracts and formats fields from a GraphQL order entry item fragment,
 * including pricing (converted from micros to display currency), gross profit percentage,
 * pack size information, and availability labels. Dynamic price edit fields are also
 * processed into a key-value map for flexible column rendering.
 *
 * @param item - A single order entry item fragment from the GraphQL API containing
 *   variant pack info, pricing, item details, and optional labels
 * @returns A formatted row data object ready for rendering in the order entry table
 *
 * @example
 * ```ts
 * const apiItem = {
 *   variant_pack_id: 'vp-456',
 *   variant_pack: {
 *     external_item_id: 'EXT-002',
 *     pack: { unit: 'CASE', min_unit_size: 24, max_unit_size: null },
 *   },
 *   variant_pack_info: {
 *     pack_price_in_micros: 4599000,
 *     currency: 'USD',
 *     gross_profit_in_percentage: 18.5,
 *   },
 *   item_info: {
 *     name: 'Premium Coffee Beans',
 *     description: 'Arabica blend, dark roast',
 *     image_url: 'https://cdn.example.com/coffee.png',
 *   },
 *   order_guide_collection_ids: ['col-a', 'col-b'],
 *   labels: {
 *     availability_label: { i18n_key: 'low_stock', level: 'WARNING' },
 *   },
 *   price_edit_display_info: [
 *     { key: 'landed_cost', value: '35.00', currency: 'USD' },
 *     { key: 'case_count', value: '24', currency: null },
 *   ],
 * };
 *
 * const rowData = transformOrderEntryItem(apiItem, 0);
 * // Returns:
 * // {
 * //   variantPackUUID: 'vp-456',
 * //   externalItemId: 'EXT-002',
 * //   name: 'Premium Coffee Beans',
 * //   description: 'Arabica blend, dark roast',
 * //   packDisplayString: '24 CT',
 * //   priceDisplayString: '$45.99',
 * //   grossProfitPercentageDisplayString: '18.5%',
 * //   priceMicros: 4599000,
 * //   imageUrl: 'https://cdn.example.com/coffee.png',
 * //   orderGuideCollectionIds: ['col-a', 'col-b'],
 * //   priceEditDisplays: {
 * //     landed_cost: { display: '$35.00', raw: 35 },
 * //     case_count: { display: '24', raw: '24' },
 * //   },
 * //   labels: {
 * //     availability_label: { i18n_key: 'low_stock', level: BadgeVariant.WARNING },
 * //   },
 * // }
 * ```
 */
export const transformOrderEntryItem = (
  item: SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment,
  sortRank: number,
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[] = EMPTY_PRICE_EDIT_DISPLAY_HEADERS,
): OrderEntryTableRowData => {
  const pack = item.variant_pack?.pack;
  const micros = item.variant_pack_info?.pack_price_in_micros ?? null;
  const priceDisplayString =
    micros === null
      ? ''
      : item.variant_pack_info?.pack_price_display ||
        formatCurrency(micros, item.variant_pack_info?.currency);
  const gp = item.variant_pack_info?.gross_profit_in_percentage ?? null;
  const grossProfitPercentageDisplayString = `${formatGrossProfitDisplayValue(gp)}%`;

  // GP$ = Price - Cost
  const costMicros = item.variant_pack_info?.pack_cost_in_micros ?? null;
  const grossProfitAmountInMicros =
    PricingCalculations.grossProfitAmountFromPrice(micros, costMicros);
  const grossProfitDollarDisplayString =
    grossProfitAmountInMicros !== null
      ? item.variant_pack_info?.gross_profit_amount_display ||
        formatCurrency(
          grossProfitAmountInMicros,
          item.variant_pack_info?.currency,
        )
      : '';

  // Fields in price_edit_display_info are dynamic so they need to be mapped here.
  // Each header.key references a cell here; values are formatted for display
  // and parsed for sorting via the shared `toPriceEditDisplayCell` helper.
  const priceEditDisplayKeyValues = item.price_edit_display_info?.reduce<
    Record<string, PriceEditDisplayCell>
  >((acc, info) => {
    acc[info.key] = toPriceEditDisplayCell(info);
    return acc;
  }, {});

  const availabilityLabel = item.labels?.availability_label
    ? {
        i18n_key: item.labels.availability_label.i18n_key,
        level: LEVEL_TO_VARIANT_MAP[item.labels.availability_label.level],
      }
    : undefined;

  const essentialItemLabel = item.labels?.essential_item_label
    ? {
        i18n_key: item.labels.essential_item_label.i18n_key,
        level: LEVEL_TO_VARIANT_MAP[item.labels.essential_item_label.level],
      }
    : undefined;

  const leadTimeLabel = item.labels?.lead_time_label?.display_text
    ? {
        displayText: getTranslationValue(
          sanitizeTranslation(item.labels.lead_time_label.display_text),
        ),
        level: LEVEL_TO_VARIANT_MAP[item.labels.lead_time_label.level],
      }
    : undefined;

  const labels = {
    availability_label: availabilityLabel,
    essential_item_label: essentialItemLabel,
    lead_time_label: leadTimeLabel,
  };

  const orderGuideLabels = (item.labels?.order_guide_labels ?? []).map(
    (og) => ({
      level: og.level,
      orderGuideName: og.order_guide_name,
    }),
  );

  // Build override info when the item has an active price override
  const priceEditInfo = item.variant_pack_info?.price_edit_info;
  const activeOverride = priceEditInfo?.active_override ?? null;
  const defaultPackPriceMicros =
    item.variant_pack_info?.default_pack_price_in_micros ?? null;

  const hasActiveOverride = activeOverride && defaultPackPriceMicros !== null;
  const currency = item.variant_pack_info?.currency || DEFAULT_CURRENCY_CODE;
  const defaultUnitPriceMicros =
    item.variant_pack_info?.default_unit_price_in_micros ?? null;
  const unit = pack?.base_unit as PackUnit | null;
  const unitSuffix = unit ? getAbbreviatedPackUnit(unit) : undefined;
  const packCostMicros = item.variant_pack_info?.pack_cost_in_micros ?? null;
  const defaultGp =
    packCostMicros !== null &&
    defaultPackPriceMicros !== null &&
    defaultPackPriceMicros > 0
      ? convertPriceMicrosToGrossProfit(packCostMicros, defaultPackPriceMicros)
      : null;

  // Overrides are only displayed as Temporary when the expiration is set
  // manually by a sales rep. Default expirations configured on the supplier
  // should be displayed normally to differentiate expirations set by the system
  // vs by a sales rep.
  const isTemporaryOverride =
    !!priceEditInfo?.expires_at &&
    priceEditInfo?.expiration_source ===
      SalesRepOverrideExpirationSource.SalesRepTemporary;
  const expiryDateDisplay =
    isTemporaryOverride && priceEditInfo?.expires_at
      ? formatSummaryDateWithYear(
          moment.tz(
            moment.parseZone(priceEditInfo.expires_at).format('YYYY-MM-DD'),
            moment.tz.guess(),
          ),
        )
      : undefined;

  const priceOverrideInfo: PriceOverrideInfo | undefined = hasActiveOverride
    ? {
        activeOverride,
        defaultGrossProfitDisplay:
          defaultGp !== null ? toFixedDecimalPercentage(defaultGp) : '',
        defaultPackPriceDisplay: formatCurrency(
          defaultPackPriceMicros!,
          currency,
        ),
        defaultUnitPriceDisplay:
          formatUnitPriceDisplay(
            defaultUnitPriceMicros,
            currency,
            unitSuffix,
          ) || undefined,
        editedByName: priceEditInfo?.price_edited_by_user_name ?? null,
        expiryDateDisplay,
        isTemporaryOverride,
      }
    : undefined;

  const requestNotes: OrderEntryRequestNote[] = (item.request_notes ?? []).map(
    (note) => ({
      collectionId: note.collection_id,
      persistRequest: note.persist_request,
      request: note.request ?? null,
    }),
  );

  const unitPriceInfo = getUnitPriceInfo(item);

  return {
    addedAt: item.order_info?.added_at ?? null,
    brand: item.item_info?.brand ?? '',
    description: item.item_info?.description ?? '',
    externalItemId: item.variant_pack?.external_item_id ?? '',
    grossProfitAmountInMicros,
    grossProfitDollarDisplayString,
    grossProfitPercentage: gp,
    grossProfitPercentageDisplayString: gp
      ? grossProfitPercentageDisplayString
      : '',
    imageUrl: item.item_info?.image_url ?? undefined,
    labels,
    maxQuantity: item.variant_pack_info?.max_quantity ?? 0,
    minQuantity: item.variant_pack_info?.min_quantity ?? 0,
    name: item.item_info?.name ?? '',
    onHandQuantity: item.variant_pack_info?.on_hand_quantity ?? null,
    orderGuideCollectionIds: item.order_guide_collection_ids ?? [],
    orderGuideLabels,
    packDisplayString: getPackDisplayString({
      abbreviated: true,
      maxUnitSize: pack?.max_unit_size ?? null,
      minUnitSize: pack?.min_unit_size ?? null,
      unit: (pack?.unit as PackUnit) ?? null,
    }),
    packSize: item.item_info?.pack_size ?? '',
    priceDisplayString,
    priceEditDisplayHeaders,
    priceEditDisplays: priceEditDisplayKeyValues ?? {},
    priceMicros: micros,
    priceOverrideInfo,
    requestNotes,
    sortRank,
    unitCount: item.variant_pack_info?.unit_count ?? 1,
    unitPriceInfo,
    unitSuffix: unitPriceInfo ? unitSuffix : undefined,
    variantPackUUID: item.variant_pack_id,
  };
};

/**
 * Transforms an array of raw order entry items from the API into table row data
 * suitable for rendering in the order entry table.
 *
 * @param items - Array of order entry item fragments from the GraphQL API
 * @returns Array of transformed row data with formatted display strings
 *
 * @example
 * ```ts
 * const apiItems = [
 *   {
 *     variant_pack_id: 'vp-123',
 *     variant_pack: { external_item_id: 'EXT-001', pack: { unit: 'CASE', min_unit_size: 12 } },
 *     variant_pack_info: { pack_price_in_micros: 2499000, currency: 'USD', gross_profit_in_percentage: 25 },
 *     item_info: { name: 'Organic Tomatoes', description: 'Fresh organic tomatoes', image_url: 'https://...' },
 *     order_guide_collection_ids: ['col-1'],
 *     labels: null,
 *     price_edit_display_info: [],
 *   },
 * ];
 *
 * const tableRows = transformOrderEntryItems(apiItems);
 * // Returns:
 * // [
 * //   {
 * //     variantPackUUID: 'vp-123',
 * //     externalItemId: 'EXT-001',
 * //     name: 'Organic Tomatoes',
 * //     description: 'Fresh organic tomatoes',
 * //     packDisplayString: '12 CT',
 * //     priceDisplayString: '$24.99',
 * //     grossProfitPercentageDisplayString: '25%',
 * //     priceMicros: 2499000,
 * //     imageUrl: 'https://...',
 * //     orderGuideCollectionIds: ['col-1'],
 * //     priceEditDisplays: {},
 * //     labels: undefined,
 * //   },
 * // ]
 * ```
 */
export const transformOrderEntryItems = (
  items: SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment[],
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[] = EMPTY_PRICE_EDIT_DISPLAY_HEADERS,
): OrderEntryTableRowData[] => {
  return items.map((item, index) =>
    transformOrderEntryItem(item, index, priceEditDisplayHeaders),
  );
};
